import styles from './edit-email.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { Col, Row } from 'reactstrap';
import { useContext, useState } from 'react';
import PageTitle from '../page-title/page-title';
import { Accordion, AccordionDetails, AccordionSummary, AvatarOnlyBorderPrimary, Box, CheckboxLabel, Container, Froala, Icon, Link, List, ListItem, ListItemAvatar, ListItemText, MenuItem, MultiLineTextField, PrimaryButton, SecondaryButton, Select, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import EditEmailSkeleton from './edit-email.skeleton';
import { IEmailComponent } from '../_models/email-component';
import { IEmailTriggerTemplate } from '../_models/email-trigger-template';
import { IEmailTrigger } from '../_models/email-trigger';
import { LanguageEnum } from '../_enums/language';
import { useTranslation } from 'react-i18next';
import { GetCurrentCompany } from '@frontend-workspace/webkua-lib';

/* eslint-disable-next-line */
export interface EditEmailProps { }

export function EditEmail(props: EditEmailProps) {
  const { templateId } = useParams();
  const { data, getData } = useGetRequestNewHook(`admin/emails/${templateId}`, undefined);
  const { data: triggerTemplates }: { data: IEmailTriggerTemplate[] } = useGetRequestNewHook(`admin/emailTriggerTemplates/`, []);
  const { data: emailComponents }: { data: IEmailComponent[] } = useGetRequestNewHook(`admin/emailComponents/`, []);
  return data && triggerTemplates.length > 0 && emailComponents.length > 0 ? <EditEmailContent
    htmlContent={data.content}
    templateId={data.templateId}
    versionId={data.versionId}
    emailTriggers={data.emailTriggers}
    triggerTemplates={triggerTemplates}
    emailName={data.templateName}
    emailSubject={data.subject}
    isEnglish={data.isEnglishTrigger}
    emailComponents={emailComponents}
    getData={getData}
  /> : <EditEmailSkeleton />;
}

export function EditEmailContent(props: {
  htmlContent: string,
  templateId: string,
  versionId: string,
  emailTriggers?: IEmailTrigger[],
  emailName: string,
  emailSubject: string,
  isEnglish: boolean,
  triggerTemplates: IEmailTriggerTemplate[],
  emailComponents?: IEmailComponent[],
  getData: () => void
}) {
  const currentCompany = GetCurrentCompany();
  const companyNumber = currentCompany ? currentCompany.number : "";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const makeRequest = useMakeRequestHook();

  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [htmlContent, setHtmlContent] = useState<string | undefined>(props.htmlContent);
  const [selectedTrigger, setSelectedTrigger] = useState<IEmailTrigger | undefined>(props.emailTriggers?.[0]);
  const [daysAfterEvent, setDaysAfterEvent] = useState<number>(selectedTrigger?.daysAfterEvent || 1);
  const [language, setLanguage] = useState<LanguageEnum>(props.isEnglish ? LanguageEnum.ENG : LanguageEnum.NOR);
  const [selectedTriggerTemplate, setSelectedTriggerTemplate] = useState<IEmailTriggerTemplate | undefined>(selectedTrigger?.triggerTemplateId ?
    props.triggerTemplates?.find(g => g.id === selectedTrigger?.triggerTemplateId) :
    undefined);
  const [name, setName] = useState<string | undefined>(props.emailName);
  const [subject, setSubject] = useState<string | undefined>(props.emailSubject);

  const handleSetTemplate = (event: any) => setSelectedTriggerTemplate(props.triggerTemplates.find(g => g.id === event.target.value));
  const handleSetTrigger = (event: any) => setSelectedTrigger(props.emailTriggers?.find(g => g.id === event.target.value));
  const handleSetLanguage = (event: any, checked: boolean) => setLanguage(checked ? LanguageEnum.ENG : LanguageEnum.NOR);

  const handleUpdateEmail = () => makeRequest(`emails`, 'PUT', {
    newContent: htmlContent,
    subject: subject,
    emailName: name,
    templateId: props.templateId,
    versionId: props.versionId
  }).then(() => {
    setSnackbarMessage({ content: t("editEmails.emailUpdated"), type: "success" });
  });

  const createTrigger = () => makeRequest(`emails/action/createTrigger`, 'PUT', {
    daysAfterEvent,
    triggerTemplateId: selectedTriggerTemplate?.id,
    language: language,
    id: selectedTrigger?.id,
    templateId: props.templateId
  }).then(result => {
    setSnackbarMessage({ content: t("editEmails.triggerMade"), type: 'success' });
    props.getData();
  });

  const sendTestEmail = () => makeRequest(`emails/${props.templateId}/action/sendTestEmail`, 'POST').then(result => {
    setSnackbarMessage({ content: t("editEmails.testEmailSent"), type: 'success' });
  });

  const handleDeleteTrigger = () => makeRequest(`emails/action/deleteTrigger/${selectedTrigger?.id}`, 'DELETE').then(result => {
    setSnackbarMessage({ content: t("editEmails.triggerDeleted"), type: 'success' });
    props.getData();
  });

  const handleBackNavigation = () => navigate(`/other/${companyNumber}/email`);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <Link onClick={handleBackNavigation} className='d-inline-flex mb-3' underline='hover'>
              <Icon className='me-1'>arrow_back</Icon>{t('app.emails')}
            </Link>
            <PageTitle title={t("standard.emails")} subtitle={`(${t("standard.changeEmail")})`} />
            <Row className='mb-3'>
              <Col lg={6} xxl={4}>
                <TextField
                  value={subject}
                  onChange={(event: any) => setSubject(event.target.value)}
                  label={`${t("standard.emailSubject")} (${t("editEmails.shownToCustomer")})`}
                  name='subject'
                  fullWidth
                  className='mb-3 mb-lg-0'
                />
              </Col>
              <Col lg={6} xxl={4}>
                <TextField
                  value={name}
                  onChange={(event: any) => setName(event.target.value)}
                  label={t("editEmails.emailInternalName")}
                  name='name'
                  fullWidth
                />
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col lg={12} xxl={8} className='mb-3 mb-xxl-0'>
                <Froala
                  maxHeight={800}
                  model={htmlContent}
                  onModelChanged={setHtmlContent}
                />
              </Col>
            </Row>
            <Row>
              <Col lg={12} xxl={4}>
                <label>{t("standard.components")}</label>
                <div className='d-flex mb-3'>
                  <List dense>
                    {props.emailComponents?.map(g => {
                      return <ListItem key={g.id}>
                        <ListItemAvatar>
                          <AvatarOnlyBorderPrimary variant='circular'>
                            <Icon>category</Icon>
                          </AvatarOnlyBorderPrimary>
                        </ListItemAvatar>
                        <ListItemText
                          primary={g.name}
                          className='me-3'
                        />
                        <SecondaryButton
                          variant='outlined'
                          size='small'
                          onClick={() => setHtmlContent(htmlContent + `${g.htmlContent}`)}>
                          <Icon className="me-1" fontSize="small">add</Icon>{t("standard.add")}
                        </SecondaryButton>
                      </ListItem>
                    })}
                  </List>
                </div>
              </Col>
            </Row>
            <Row className='mb-5'>
              <Col>
                <PrimaryButton
                  variant='contained'
                  onClick={handleUpdateEmail}>
                  {t("editEmails.updateEmailContent")}
                </PrimaryButton>
              </Col>
            </Row>
            <Row>
              <Col lg={6} xxl={6}>
                <div className='d-flex mb-3'>
                  {selectedTriggerTemplate?.isOrder ?
                    <TextField
                      disabled={true}
                      value={selectedTriggerTemplate?.eventName || ""}
                      label={t("editEmails.typeOfTrigger")}
                      name='eventName'
                      fullWidth
                    />
                    :
                    <Select
                      displayEmpty
                      value={selectedTriggerTemplate?.id || ""}
                      onChange={handleSetTemplate}
                      label={t("standard.selectTriggerType")}
                      name='triggerTemplate'
                      className={"me-5"}
                      sx={{ minWidth: '150px' }}>
                      {props.triggerTemplates.filter(g => !g.isOrder).map(g =>
                        <MenuItem
                          value={g.id}
                          key={g.id}>
                          {g.eventName} {g.isOrder ? "" : `(${t("editEmails.trigger")})`} {g.missingEnglishEmail && g.isOrder ? `(${t("editEmails.missingEnglishEmail")})` : ""} {g.missingNorwegianEmail && g.isOrder ? `(${t("editEmails.missingNorwegianEmail")})` : ""}
                        </MenuItem>)}
                    </Select>}
                </div>
                {selectedTriggerTemplate && <>
                  {!selectedTriggerTemplate.isOrder &&
                    <Select
                      displayEmpty
                      value={selectedTrigger?.id || ""}
                      onChange={handleSetTrigger}
                      label={t("editEmails.selectTrigger")}
                      name='trigger'
                      className='mb-3'>
                      <MenuItem
                        value=""
                        key={"new"}>
                        {t("editEmails.newTrigger")}
                      </MenuItem>
                      {props.emailTriggers?.map(g =>
                        <MenuItem
                          value={g.id}
                          key={g.id}>
                          {g.daysAfterEvent} {t("standard.days")}
                        </MenuItem>)}
                    </Select>}
                  <MultiLineTextField
                    fullWidth
                    label={t("standard.triggerDescription")}
                    name='description'
                    disabled={true}
                    className='mb-3'
                    value={selectedTriggerTemplate?.description || ""}
                  />
                  {<TextField
                    value={selectedTriggerTemplate?.recepientDescription || ""}
                    label={t("editEmails.emailRecipient")}
                    name='recepientDescription'
                    disabled={true}
                    fullWidth
                    className='mb-3'
                  />}
                  <div className='mb-3'>
                    <CheckboxLabel
                      label={t("editEmails.forEnglishLanguage")}
                      onChange={handleSetLanguage}
                      disabled={selectedTriggerTemplate?.isOrder}
                      checked={language === LanguageEnum.ENG}
                    />
                  </div>
                  {!selectedTriggerTemplate.isOrder &&
                    <TextField
                      type='number'
                      label={t("editEmails.numberOfDaysBetweenTriggerAndEvent")}
                      name='daysAfterEvent'
                      value={daysAfterEvent}
                      onChange={(event: any) => setDaysAfterEvent(event.target.value)}
                      className='mb-3'
                    />}
                  <div className='mb-3'>
                    {selectedTriggerTemplate?.variables && Object.keys(selectedTriggerTemplate?.variables).map((key) => {
                      //@ts-ignore
                      const content = selectedTriggerTemplate?.variables[key];
                      if (!content) return null;
                      return (
                        <Accordion key={key}>
                          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                            {key} {t("standard.variables")}
                          </AccordionSummary>
                          <AccordionDetails>
                            <List dense>
                              {Object.keys(content).map((innerKey) => {
                                return <EmailVariable key={innerKey} name={content[innerKey]} handle={innerKey} />;
                              })}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </div>
                  <PrimaryButton
                    variant='contained'
                    onClick={createTrigger}
                    className={"me-3"}>
                    {selectedTrigger?.id ? t("editEmails.updateTrigger") : t("editEmails.createTrigger")}
                  </PrimaryButton>
                  <SecondaryButton
                    variant='contained'
                    onClick={sendTestEmail}
                    className={"me-5"}>
                    {t("editEmails.sendTestEmail")}
                  </SecondaryButton>
                </>}
                {selectedTrigger && !selectedTriggerTemplate?.isOrder &&
                  <SecondaryButton
                    variant='contained'
                    onClick={handleDeleteTrigger}>
                    {t("editEmails.deleteTrigger")}
                  </SecondaryButton>}
              </Col>
            </Row>
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export function EmailVariable(props: {
  name: string,
  handle: string
}) {
  return <ListItem>
    <ListItemText
      primary={`${props.name} - {{${props.handle}}}`}
      primaryTypographyProps={{
        color: '#6c757d !important',
      }}
    />
  </ListItem>
}

export default EditEmail;